'use client'

import { forwardRef, useRef, useImperativeHandle } from 'react'
import MuxPlayer from '@mux/mux-player-react'
import '@mux/mux-player/themes/minimal'
import type MuxPlayerElement from '@mux/mux-player'
import { getVideoThumbnail } from '../data/videos'

export type VideoPlayerRef = {
  play: () => Promise<void>
  pause: () => void
  getCurrentTime: () => number
  setCurrentTime: (time: number) => void
  getDuration: () => number
  isPaused: () => boolean
  getElement: () => MuxPlayerElement | null
}

export type VideoPlayerProps = {
  playbackId: string
  autoPlay?: boolean
  muted?: boolean
  loop?: boolean
  playsInline?: boolean
  showControls?: boolean
  posterTime?: number
  aspectRatio?: 'portrait' | 'landscape' | 'square'
  className?: string
  onPlay?: () => void
  onPause?: () => void
  onTimeUpdate?: (currentTime: number) => void
  onLoadedMetadata?: (duration: number) => void
  onEnded?: () => void
}

export const VideoPlayer = forwardRef<VideoPlayerRef, VideoPlayerProps>(function VideoPlayer(
  {
    playbackId,
    autoPlay = false,
    muted = false,
    loop = false,
    playsInline = true,
    showControls = false,
    posterTime = 0,
    aspectRatio = 'portrait',
    className = '',
    onPlay,
    onPause,
    onTimeUpdate,
    onLoadedMetadata,
    onEnded,
  },
  ref
) {
  const playerRef = useRef<MuxPlayerElement>(null)

  useImperativeHandle(ref, () => ({
    play: async () => {
      await playerRef.current?.play()
    },
    pause: () => {
      playerRef.current?.pause()
    },
    getCurrentTime: () => playerRef.current?.currentTime ?? 0,
    setCurrentTime: (time: number) => {
      if (playerRef.current) {
        playerRef.current.currentTime = time
      }
    },
    getDuration: () => playerRef.current?.duration ?? 0,
    isPaused: () => playerRef.current?.paused ?? true,
    getElement: () => playerRef.current,
  }))

  const aspectRatioClass = {
    portrait: '[aspect-ratio:400/720]',
    landscape: '[aspect-ratio:16/9]',
    square: '[aspect-ratio:1/1]',
  }[aspectRatio]

  return (
    <MuxPlayer
      ref={playerRef}
      playbackId={playbackId}
      streamType="on-demand"
      accentColor="#1A1A1A"
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      preload="metadata"
      poster={getVideoThumbnail(playbackId, posterTime)}
      className={`w-full h-full ${aspectRatioClass} ${showControls ? '' : '[--controls:none]'} pointer-events-auto ${className}`}
      onPlay={onPlay}
      onPause={onPause}
      onTimeUpdate={() => onTimeUpdate?.(playerRef.current?.currentTime ?? 0)}
      onLoadedMetadata={() => onLoadedMetadata?.(playerRef.current?.duration ?? 0)}
      onEnded={onEnded}
    />
  )
})
